import moment from 'moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { useLocalizedCalendarDay } from '@treams/treams-hooks';
import { CalendarIcon, RepeatIcon } from '@treams/treams-icons';
import { NoteFragment } from '@treams/treams-network';
import { Button, FlexGap, InlineIconWrapper, Popover, Subheading, tokens } from '@treams/treams-ui';

import NotePanel from './NotePanel';
import { OneOnOneMeetingModal } from './OneOnOneMeetingModal';

const DayCell = styled.div<{ $isToday?: boolean; $isMeeting?: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 2px;
	flex: 1 1 0;
	min-width: 0;
	padding: 6px 2px;
	border-radius: 8px;
	border: 1px solid transparent;
	background: transparent;
	color: ${tokens.text.primary};

	${(p) =>
		p.$isToday &&
		!p.$isMeeting &&
		css`
			background: ${tokens.surface.white};
			border-color: ${tokens.border.default};
		`}

	${(p) =>
		p.$isMeeting &&
		css`
			background: ${tokens.text.primary};
			border-color: ${tokens.text.primary};
			color: ${tokens.text.inverse};
		`}
`;

const DayLabel = styled.span`
	font-size: ${tokens.typography.sizes.small}px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	opacity: 0.75;
`;

const DayNumber = styled.span`
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: 600;
	line-height: 1;
`;

export default function NoteWeekAgenda({
	note,
	subject,
}: {
	note: NoteFragment;
	subject: NonNullable<NoteFragment['subject']>;
}) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	const days = useMemo(() => {
		const start = moment().startOf('week');
		return Array.from({ length: 7 }, (_, i) => moment(start).add(i, 'day'));
	}, []);

	const meetingDate = note.meeting?.startsAt ? moment(note.meeting.startsAt) : undefined;
	const formattedMeetingDate = useLocalizedCalendarDay(meetingDate, {
		includeTime: true,
		capitalize: true,
	});
	const today = moment();

	const meetingInThisWeek =
		!!meetingDate &&
		meetingDate.isSameOrAfter(days[0], 'day') &&
		meetingDate.isSameOrBefore(days[6], 'day');

	const buttonTitle = useMemo(() => {
		if (note.viewerCanUpdate) {
			return note.meeting
				? meetingInThisWeek
					? t('oneOnOne.weekAgenda.reschedule')
					: t('oneOnOne.weekAgenda.scheduleNext')
				: t('oneOnOne.meeting.planMeeting');
		}

		return note.meeting ? t('oneOnOne.meeting.viewDetails') : t('oneOnOne.meeting.notPlanned');
	}, [note.meeting, meetingInThisWeek, note.viewerCanUpdate, t]);

	return (
		<NotePanel>
			<Subheading>
				{meetingDate ? (
					<>
						{formattedMeetingDate}
						{note.meeting?.series && (
							<InlineIconWrapper translateY={-2} ml={2}>
								<RepeatIcon />
							</InlineIconWrapper>
						)}
					</>
				) : (
					<>{t('oneOnOne.weekAgenda.title')}</>
				)}
			</Subheading>
			<FlexGap gap={4} alignItems="stretch">
				{days.map((day) => {
					const isToday = day.isSame(today, 'day');
					const isMeeting = !!meetingDate && day.isSame(meetingDate, 'day');
					return (
						<DayCell key={day.toISOString()} $isToday={isToday} $isMeeting={isMeeting}>
							<DayLabel>{day.format('dd')}</DayLabel>
							<DayNumber>{day.format('D')}</DayNumber>
						</DayCell>
					);
				})}
			</FlexGap>

			<Popover.Target
				center
				contents={
					<OneOnOneMeetingModal
						key={open.toString()}
						note={note}
						subject={subject}
						onClose={() => setOpen(false)}
					/>
				}
				visible={open}
				onDismiss={() => setOpen(false)}
			>
				<Button
					type="secondary"
					width="100%"
					icon={<CalendarIcon />}
					title={buttonTitle}
					onClick={() => setOpen(true)}
					disabled={!note.viewerCanUpdate && !note.meeting}
				/>
			</Popover.Target>
		</NotePanel>
	);
}
